import React, { useEffect } from 'react';
import { useField } from 'formik';
import { ProviderCurrency, ProviderTrustTier } from 'types/provider';
import { Header, Table } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import DropdownField from 'components/form-fields/formik/DropdownField';
import PriceInput from 'components/form-fields/Price';

// Define the maximum limits for trust tiers by currency, type, and level
export const TRUST_TIER_LIMITS = {
  individual: {
    EUR: { 1: 250, 2: 500, 3: 1000, 4: 2000 },
    GBP: { 1: 220, 2: 440, 3: 875, 4: 1750 },
    DKK: { 1: 2400, 2: 4750, 3: 7500, 4: 15000 },
    CHF: { 1: 240, 2: 475, 3: 950, 4: 1900 },
  },
  organization: {
    EUR: { 1: 3750, 2: 7500, 3: 15000, 4: 30000 },
    GBP: { 1: 3250, 2: 6500, 3: 13000, 4: 26000 },
    DKK: { 1: 28000, 2: 56000, 3: 112000, 4: 224000 },
    CHF: { 1: 3500, 2: 7000, 3: 14000, 4: 28000 },
  },
};

export const ALLOWED_TRUST_TIERS_NUMBER = 5;

export function ProviderTrustTiersForm() {
  const { t } = useTranslation();

  const dropdownOptions = Array.from(
    { length: ALLOWED_TRUST_TIERS_NUMBER },
    (_, i) => ({
      key: i,
      text:
        t('editProvider.trustTiers.name', 'Tier {{TIER_NUMBER}}', {
          TIER_NUMBER: i,
        }) +
        (i === 0
          ? ` (${t('editProvider.trustTiers.NoTrust', 'No Trust')})`
          : ''),
      value: i,
    })
  );

  return (
    <div>
      <Header
        icon="sliders"
        content={t('editProvider.trustTiers.title', 'MSP Limits')}
        subheader={t(
          'editProvider.trustTiers.description',
          'Configure Tiers for Individuals & Organizations'
        )}
        as="h4"
        dividing
      />

      <TrustTiersTable />

      <DropdownField
        fluid
        name="trustTiers.defaultForNewIndividuals"
        selection
        options={dropdownOptions}
        label={t(
          'editProvider.trustTiers.defaultForNewIndividuals',
          'Default Tier for New Individual Accounts'
        )}
      />

      <DropdownField
        name="trustTiers.defaultForNewOrganizations"
        fluid
        selection
        options={dropdownOptions}
        label={t(
          'editProvider.trustTiers.defaultForNewOrganizations',
          'Default Tier for New Organization Accounts'
        )}
      />
    </div>
  );
}

function TrustTiersTable() {
  const { t } = useTranslation();
  const [trustTierField, , trustTierHelpers] =
    useField<ProviderTrustTier[]>('trustTiers.tiers');
  const [currenciesField] = useField<ProviderCurrency[]>('currencies');

  const trustTiers = trustTierField;
  const currencies = currenciesField.value.filter(
    (c) => c.currency && c.currency.length > 0
  );

  const updateTrustTiers = (newValues: ProviderTrustTier[]) => {
    // First, update the form state
    trustTierHelpers.setValue(newValues);
  };

  useEffect(() => {
    // Append non-existing currencies to trust tiers
    const existingCurrencies = new Set(trustTiers.value.map((e) => e.currency));
    const newTiers: ProviderTrustTier[] = [];

    for (const currency of currencies) {
      if (!existingCurrencies.has(currency.currency)) {
        for (let tier = 0; tier < ALLOWED_TRUST_TIERS_NUMBER; tier++) {
          // Individual tier
          const individualMaxLimit =
            tier === 0
              ? 0
              : TRUST_TIER_LIMITS.individual[
                  currency.currency as keyof typeof TRUST_TIER_LIMITS.individual
                ]?.[tier as keyof typeof TRUST_TIER_LIMITS.individual.EUR] || 0;

          newTiers.push({
            level: tier,
            limit: individualMaxLimit,
            type: 'individual',
            currency: currency.currency,
          });

          // Organization tier
          const organizationMaxLimit =
            tier === 0
              ? 0
              : TRUST_TIER_LIMITS.organization[
                  currency.currency as keyof typeof TRUST_TIER_LIMITS.organization
                ]?.[tier as keyof typeof TRUST_TIER_LIMITS.organization.EUR] ||
                0;

          newTiers.push({
            level: tier,
            limit: organizationMaxLimit,
            type: 'organization',
            currency: currency.currency,
          });
        }
      }
    }

    if (newTiers.length > 0) {
      updateTrustTiers([...trustTiers.value, ...newTiers]);
    }
  }, [currencies]);

  if (!trustTiers.value || trustTiers.value.length === 0) {
    const defaultLimits = generateDefaultLimits(
      currencies.map((c) => c.currency)
    );
    updateTrustTiers(defaultLimits);
    return <></>;
  }

  const rows: React.ReactElement[] = [];

  for (let tier = 0; tier < ALLOWED_TRUST_TIERS_NUMBER; tier++) {
    let showName = true;
    for (const currency of currencies) {
      rows.push(
        <Table.Row key={`row-${tier}-${currency.currency}`}>
          {showName && (
            <Table.Cell rowSpan={currencies.length}>
              {t('editProvider.trustTiers.TierName', 'Tier {{TIER_NUMBER}}', {
                TIER_NUMBER: tier,
              })}{' '}
              {tier === 0 &&
                `(${t('editProvider.trustTiers.NoTrust', 'No Trust')})`}
            </Table.Cell>
          )}
          <Table.Cell>{currency.currency}</Table.Cell>
          <Table.Cell>
            <PriceInput
              disabled={tier === 0}
              value={getLimitValue(
                trustTiers.value,
                currency.currency,
                'individual',
                tier
              )}
              currency={currency.currency}
              max={getMaxAllowedLimit(currency.currency, 'individual', tier)}
              onChange={(value) => {
                const newTrustTiers = trustTiers.value.map((e) =>
                  e.currency === currency.currency &&
                  e.type === 'individual' &&
                  e.level === tier
                    ? { ...e, limit: value }
                    : e
                );
                updateTrustTiers(newTrustTiers);
              }}
            />
          </Table.Cell>
          <Table.Cell>
            <PriceInput
              disabled={tier === 0}
              value={getLimitValue(
                trustTiers.value,
                currency.currency,
                'organization',
                tier
              )}
              currency={currency.currency}
              max={getMaxAllowedLimit(currency.currency, 'organization', tier)}
              onChange={(value) => {
                const newTrustTiers = trustTiers.value.map((e) =>
                  e.currency === currency.currency &&
                  e.type === 'organization' &&
                  e.level === tier
                    ? { ...e, limit: value }
                    : e
                );
                updateTrustTiers(newTrustTiers);
              }}
            />
          </Table.Cell>
        </Table.Row>
      );
      showName = false;
    }
  }

  return (
    <Table celled structured>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>
            {t('editProvider.trustTiersHeaders.name', 'Name')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t('editProvider.trustTiersHeaders.currency', 'Currency')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t(
              'editProvider.trustTiersHeaders.individualLimit',
              'Individual Limit'
            )}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t(
              'editProvider.trustTiersHeaders.organizationLimit',
              'Organization Limit'
            )}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>{rows}</Table.Body>
    </Table>
  );
}

function getLimitValue(
  trustTiers: ProviderTrustTier[],
  currency: string,
  type: 'individual' | 'organization',
  level: number
): number {
  const limit = trustTiers.find(
    (e) => e.currency === currency && e.type === type && e.level === level
  );
  return limit ? limit.limit : level === 0 ? 0 : 0; // 0 means no limit set or no trust
}

function getMaxAllowedLimit(
  currency: string,
  type: 'individual' | 'organization',
  level: number
): number | undefined {
  if (level === 0) return 0;
  return TRUST_TIER_LIMITS[type]?.[
    currency as keyof typeof TRUST_TIER_LIMITS.individual
  ]?.[level as keyof typeof TRUST_TIER_LIMITS.individual.EUR];
}

export function generateDefaultLimits(
  currencies: string[]
): ProviderTrustTier[] {
  const tiers: ProviderTrustTier[] = [];

  for (let tier = 0; tier < ALLOWED_TRUST_TIERS_NUMBER; tier++) {
    for (const currency of currencies) {
      // For individual tiers
      const individualMaxLimit =
        tier === 0
          ? 0
          : TRUST_TIER_LIMITS.individual[
              currency as keyof typeof TRUST_TIER_LIMITS.individual
            ]?.[tier as keyof typeof TRUST_TIER_LIMITS.individual.EUR] || 0;

      tiers.push({
        level: tier,
        limit: individualMaxLimit,
        type: 'individual',
        currency,
      });

      // For organization tiers
      const organizationMaxLimit =
        tier === 0
          ? 0
          : TRUST_TIER_LIMITS.organization[
              currency as keyof typeof TRUST_TIER_LIMITS.organization
            ]?.[tier as keyof typeof TRUST_TIER_LIMITS.organization.EUR] || 0;

      tiers.push({
        level: tier,
        limit: organizationMaxLimit,
        type: 'organization',
        currency,
      });
    }
  }

  return tiers;
}
